import { log } from '../services/production-logger';

/**
 * Initialization Manager
 * 
 * Manages the application initialization sequence to prevent circular dependencies
 * and stack overflow errors during startup.
 * 
 * Initialization phases:
 * 1. FIREBASE - Firebase SDK initialization
 * 2. AUTH - Authentication state setup
 * 3. USER_PROFILE - User profile loading
 * 4. ORGANIZATION - Organization context initialization
 */

type InitializationPhase = 'FIREBASE' | 'AUTH' | 'USER_PROFILE' | 'ORGANIZATION';

interface PhaseStatus {
  completed: boolean;
  error?: Error;
  timestamp?: number;
}

class InitializationManager {
  private static instance: InitializationManager;
  private phases: Map<InitializationPhase, PhaseStatus>;
  private listeners: Map<InitializationPhase, Array<(status: PhaseStatus) => void>>;
  private initializationStartTime: number;

  private constructor() {
    this.phases = new Map([
      ['FIREBASE', { completed: false }],
      ['AUTH', { completed: false }],
      ['USER_PROFILE', { completed: false }],
      ['ORGANIZATION', { completed: false }],
    ]);
    this.listeners = new Map();
    this.initializationStartTime = Date.now();
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): InitializationManager {
    if (!InitializationManager.instance) {
      InitializationManager.instance = new InitializationManager();
    }
    return InitializationManager.instance;
  }

  /**
   * Wait for a specific phase to complete
   * @param phase - The initialization phase to wait for
   * @param timeout - Maximum time to wait in milliseconds (default: 30 seconds)
   * @returns Promise that resolves when the phase is complete
   */
  async waitForPhase(phase: InitializationPhase, timeout: number = 30000): Promise<void> {
    const startTime = Date.now();
    
    // Check if already completed
    const status = this.phases.get(phase);
    if (status?.completed) {
      return;
    }

    // If there's an error, throw it
    if (status?.error) {
      throw status.error;
    }

    // Wait for completion
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        const currentStatus = this.phases.get(phase);
        
        if (currentStatus?.completed) {
          clearInterval(checkInterval);
          resolve();
        } else if (currentStatus?.error) {
          clearInterval(checkInterval);
          reject(currentStatus.error);
        } else if (Date.now() - startTime > timeout) {
          clearInterval(checkInterval);
          reject(new Error(`Initialization timeout: ${phase} phase did not complete within ${timeout}ms`));
        }
      }, 50); // Check every 50ms

      // Also register a listener for immediate notification
      this.addListener(phase, (status) => {
        if (status.completed || status.error) {
          clearInterval(checkInterval);
          if (status.completed) {
            resolve();
          } else if (status.error) {
            reject(status.error);
          }
        }
      });
    });
  }

  /**
   * Mark a phase as complete
   * @param phase - The phase that has completed
   */
  markComplete(phase: InitializationPhase): void {
    const status: PhaseStatus = {
      completed: true,
      timestamp: Date.now(),
    };
    
    this.phases.set(phase, status);
    this.notifyListeners(phase, status);
    
    if (__DEV__) {
      const elapsed = Date.now() - this.initializationStartTime;
      log.debug(`✅ [InitManager] ${phase} completed in ${elapsed}ms`);
    }
  }

  /**
   * Mark a phase as failed
   * @param phase - The phase that failed
   * @param error - The error that occurred
   */
  markFailed(phase: InitializationPhase, error: Error): void {
    const status: PhaseStatus = {
      completed: false,
      error,
      timestamp: Date.now(),
    };
    
    this.phases.set(phase, status);
    this.notifyListeners(phase, status);
    
    log.error(`❌ [InitManager] ${phase} failed:`, error);
  }

  /**
   * Check if a phase is complete
   * @param phase - The phase to check
   * @returns true if the phase is complete
   */
  isPhaseComplete(phase: InitializationPhase): boolean {
    return this.phases.get(phase)?.completed || false;
  }

  /**
   * Get the status of all phases
   * @returns Object with phase statuses
   */
  getStatus(): Record<InitializationPhase, PhaseStatus> {
    const status: any = {};
    this.phases.forEach((value, key) => {
      status[key] = { ...value };
    });
    return status;
  }

  /**
   * Reset the initialization state (useful for testing)
   */
  reset(): void {
    this.phases.forEach((_, phase) => {
      this.phases.set(phase, { completed: false });
    });
    this.listeners.clear();
    this.initializationStartTime = Date.now();
  }

  /**
   * Add a listener for phase changes
   */
  private addListener(phase: InitializationPhase, callback: (status: PhaseStatus) => void): void {
    const listeners = this.listeners.get(phase) || [];
    listeners.push(callback);
    this.listeners.set(phase, listeners);
  }

  /**
   * Notify all listeners of a phase change
   */
  private notifyListeners(phase: InitializationPhase, status: PhaseStatus): void {
    const listeners = this.listeners.get(phase) || [];
    listeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        log.error(`[InitManager] Listener error for ${phase}:`, error);
      }
    });
  }

  /**
   * Initialize the manager and mark FIREBASE phase as complete
   * @returns Promise that resolves when initialization is done
   */
  async initialize(): Promise<void> {
    if (__DEV__) {
      log.debug('🚀 [InitManager] Starting initialization sequence');
    }
    
    // Mark Firebase phase as complete since it should be ready by now
    this.markComplete('FIREBASE');
  }

  /**
   * Wait for all phases to complete
   * @param timeout - Maximum time to wait in milliseconds (default: 30 seconds)
   */
  async waitForAllPhases(timeout: number = 30000): Promise<void> {
    const phases: InitializationPhase[] = ['FIREBASE', 'AUTH', 'USER_PROFILE', 'ORGANIZATION'];
    await Promise.all(phases.map(phase => this.waitForPhase(phase, timeout)));
  }

  /**
   * Get initialization metrics
   */
  getMetrics(): {
    totalTime: number;
    phaseTimings: Record<string, number>;
    completed: boolean;
  } {
    const now = Date.now();
    const phaseTimings: Record<string, number> = {};
    let allCompleted = true;

    this.phases.forEach((status, phase) => {
      if (status.timestamp) {
        phaseTimings[phase] = status.timestamp - this.initializationStartTime;
      }
      if (!status.completed) {
        allCompleted = false;
      }
    });

    return {
      totalTime: now - this.initializationStartTime,
      phaseTimings,
      completed: allCompleted,
    };
  }
}

// Export singleton instance
export const initManager = InitializationManager.getInstance();

// Export types
export type { InitializationPhase, PhaseStatus };